/**
 * 开单 —— 整个系统的生死线（CP1：≤90 秒）。
 *
 * 三步都在同一个抽屉里，可随时后退不丢数据。第 3 步可跳过。
 * 设计要点：类型按钮 72px 高（最频繁的选择）、报价与交期随类型自动带出、
 * 交期快捷芯片省掉日期选择器的翻月操作。
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronUp,
  Copy,
  MessageCircle,
  Search,
  UserPlus,
  Check as CheckIcon,
} from 'lucide-react'
import {
  api,
  type Arch,
  type CaseMeta,
  type CaseType,
  type PaymentMethod,
  type SearchItem,
  type ToothMap,
} from '../lib/api'
import {
  Avatar,
  Badge,
  Button,
  Drawer,
  Field,
  Input,
  MoneyInput,
  Segmented,
  Textarea,
  cx,
  useToast,
} from '../components/ui'
import { ToothChart } from '../components/ToothChart'
import { NewCustomerDrawer } from './NewCustomerDrawer'
import { addDays, dateKey, parseAmount, rm, date as fmtDate } from '../lib/format'
import { feedback } from '../lib/feedback'

const EMPTY_TOOTH: ToothMap = { missing: [], clasp: [], replace: [] }

type Step = 1 | 2 | 3 | 'done'

export function NewCaseDrawer({
  open,
  onClose,
  presetCustomer,
}: {
  open: boolean
  onClose: () => void
  presetCustomer?: { id: string; fullName: string }
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>(1)
  const [customer, setCustomer] = useState<{ id: string; fullName: string } | null>(null)
  const [type, setType] = useState<string | null>(null)
  const [arch, setArch] = useState<Arch | null>(null)
  const [tooth, setTooth] = useState<ToothMap>(EMPTY_TOOTH)
  const [toothOpen, setToothOpen] = useState(false)
  const [price, setPrice] = useState('')
  const [promised, setPromised] = useState('')
  // 服务日期 —— 顾客实际到店那天。店里忙起来当场记不了，隔天补开单是常态。
  // 空 = 今天。
  const [openedAt, setOpenedAt] = useState('')
  const [notes, setNotes] = useState('')
  const [deposit, setDeposit] = useState('')
  const [method, setMethod] = useState<PaymentMethod>('CASH')
  const [sendLink, setSendLink] = useState(true)
  const [result, setResult] = useState<{
    caseId: string
    caseNo: string
    finalAmount: number
    paidAmount: number
    balanceAmount: number
    link: { url: string; messageText: string; whatsappUrl: string } | null
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const meta = useQuery({
    queryKey: ['case-meta'],
    queryFn: () => api.get<CaseMeta>('/api/cases/meta'),
    enabled: open,
    staleTime: 10 * 60_000,
  })

  useEffect(() => {
    if (!open) return
    setStep(presetCustomer ? 2 : 1)
    setCustomer(presetCustomer ?? null)
    setType(null)
    setArch(null)
    setTooth(EMPTY_TOOTH)
    setToothOpen(false)
    setPrice('')
    setPromised('')
    setOpenedAt('')
    setNotes('')
    setDeposit('')
    setMethod('CASH')
    setSendLink(true)
    setResult(null)
    setError(null)
  }, [open, presetCustomer])

  const typeMeta = meta.data?.types.find((t) => t.code === type)

  // 选类型 → 自动带出默认价与交期。妈妈只需要改例外。
  const pickType = (t: string) => {
    setType(t)
    const m = meta.data?.types.find((x) => x.code === t)
    setToothOpen(m?.showsToothChart ?? false)
    if (m?.defaultAmount) setPrice((m.defaultAmount / 100).toFixed(2))
    else setPrice('')
    if (m) setPromised(dateKey(addDays(new Date(), m.leadDays)))
  }

  const create = useMutation({
    mutationFn: async () => {
      const amountSen = parseAmount(price)
      if (amountSen === null) throw new Error('报价格式不正确')
      const depositSen = deposit.trim() ? parseAmount(deposit) : null
      if (deposit.trim() && depositSen === null) throw new Error('定金格式不正确')

      return api.post<{
        case: { id: string; caseNo: string; finalAmount: number; paidAmount: number; balanceAmount: number }
        portalLink: { url: string; messageText: string; whatsappUrl: string } | null
      }>('/api/cases', {
        customerId: customer!.id,
        type,
        arch,
        toothMap: tooth,
        quotedAmount: amountSen,
        openedAt: openedAt || null,
        promisedDate: promised || null,
        notes: notes.trim() || null,
        deposit:
          depositSen && depositSen > 0 ? { amount: depositSen, method } : undefined,
        createPortalLink: sendLink,
      })
    },
    onSuccess: (r) => {
      feedback('money')
      void qc.invalidateQueries({ queryKey: ['today'] })
      void qc.invalidateQueries({ queryKey: ['cases'] })
      void qc.invalidateQueries({ queryKey: ['customer'] })
      setResult({
        caseId: r.case.id,
        caseNo: r.case.caseNo,
        finalAmount: r.case.finalAmount,
        paidAmount: r.case.paidAmount,
        balanceAmount: r.case.balanceAmount,
        link: r.portalLink,
      })
      setStep('done')
    },
    onError: (e) => {
      feedback('error')
      setError((e as Error).message)
    },
  })

  const canNext2 = type !== null && parseAmount(price) !== null

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width="lg"
        title={step === 'done' ? '开单完成' : '开新单'}
        footer={
          step === 'done' ? undefined : (
            <>
              {step > 1 && (
                <Button size="lg" onClick={() => setStep((s) => ((s as number) - 1) as Step)}>
                  上一步
                </Button>
              )}
              {step === 1 && (
                <Button size="lg" variant="primary" disabled={!customer} onClick={() => setStep(2)}>
                  下一步
                </Button>
              )}
              {step === 2 && (
                <Button size="lg" variant="primary" disabled={!canNext2} onClick={() => setStep(3)}>
                  下一步
                </Button>
              )}
              {step === 3 && (
                <>
                  <Button size="lg" loading={create.isPending} onClick={() => create.mutate()}>
                    跳过，直接建单
                  </Button>
                  <Button
                    size="lg"
                    variant="primary"
                    loading={create.isPending}
                    onClick={() => create.mutate()}
                  >
                    建单
                  </Button>
                </>
              )}
            </>
          )
        }
      >
        {step !== 'done' && <Steps current={step as 1 | 2 | 3} />}

        {error && (
          <div className="mb-4 rounded-md border border-bad-line bg-bad-bg px-4 py-3 text-[16px] text-bad">
            {error}
          </div>
        )}

        {step === 1 && <PickCustomer value={customer} onPick={setCustomer} />}

        {step === 2 && (
          <div className="flex flex-col gap-6">
            <Field label="类型" required>
              <div className="grid grid-cols-3 gap-2.5">
                {(meta.data?.types ?? []).map((t) => (
                  <button
                    key={t.code}
                    onClick={() => pickType(t.code)}
                    className={cx(
                      'relative flex h-18 flex-col items-center justify-center gap-1 rounded-md border text-[16px] font-semibold transition-colors',
                      type === t.code
                        ? 'border-2 border-brand-600 bg-brand-100 text-brand-700'
                        : 'border-line-strong bg-white hover:bg-sunken',
                    )}
                  >
                    {type === t.code && (
                      <CheckIcon size={16} className="absolute right-1.5 top-1.5" />
                    )}
                    {t.label.replace('义齿', '').replace('假牙', '')}
                    <span className="text-[12px] font-normal text-ink-3">
                      约 {t.leadDays} 天
                    </span>
                  </button>
                ))}
              </div>
            </Field>

            <Field label="颌位">
              <Segmented
                value={arch}
                onChange={setArch}
                ariaLabel="颌位"
                options={[
                  { value: 'UPPER', label: '上颌' },
                  { value: 'LOWER', label: '下颌' },
                  { value: 'BOTH', label: '双颌' },
                ]}
              />
            </Field>

            <div>
              <button
                onClick={() => setToothOpen((v) => !v)}
                className="flex h-11 w-full items-center gap-2 text-left text-[15px] font-semibold"
              >
                {toothOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                牙位图
                {!toothOpen && tooth.missing.length + tooth.clasp.length + tooth.replace.length > 0 && (
                  <Badge tone="brand">
                    已标 {tooth.missing.length + tooth.clasp.length + tooth.replace.length}
                  </Badge>
                )}
                {!toothOpen && !typeMeta?.showsToothChart && (
                  <span className="text-[14px] font-normal text-ink-3">
                    这类工单通常不需要
                  </span>
                )}
              </button>
              {toothOpen && (
                <div className="mt-3">
                  <ToothChart value={tooth} onChange={setTooth} compact />
                </div>
              )}
            </div>

            <Field
              label="哪天的单"
              hint="忙的时候来不及记？选顾客实际到店那天。空着就是今天。"
            >
              <Input
                type="date"
                value={openedAt}
                max={dateKey(new Date())}
                onChange={(e) => setOpenedAt(e.target.value)}
                className="tnum"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  ['今天', 0],
                  ['昨天', -1],
                  ['前天', -2],
                  ['大前天', -3],
                ].map(([label, days]) => {
                  const key = dateKey(addDays(new Date(), days as number))
                  const active = (openedAt || dateKey(new Date())) === key
                  return (
                    <button
                      key={label as string}
                      type="button"
                      onClick={() => setOpenedAt(days === 0 ? '' : key)}
                      className={cx(
                        'h-10 rounded-full border px-4 text-[15px] font-semibold transition-colors',
                        active
                          ? 'border-brand-600 bg-brand-100 text-brand-700'
                          : 'border-line text-ink-2 hover:bg-sunken',
                      )}
                    >
                      {label as string}
                    </button>
                  )
                })}
              </div>
              {openedAt && openedAt !== dateKey(new Date()) && (
                <p className="mt-2 rounded-md bg-warn-bg px-3 py-2 text-[15px] text-warn">
                  这张单会算作 {openedAt} 的单：单号按那个月编，交期也从那天起算。
                </p>
              )}
            </Field>

            <Field label="报价" required>
              <MoneyInput value={price} onChange={setPrice} invalid={!!price && parseAmount(price) === null} />
            </Field>

            <Field label="承诺交期">
              <Input type="date" value={promised} onChange={(e) => setPromised(e.target.value)} className="tnum" />
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  ['今天', 0],
                  ['+1周', 7],
                  ['+2周', 14],
                  ['+1月', 30],
                ].map(([label, days]) => {
                  const key = dateKey(addDays(new Date(), days as number))
                  return (
                    <button
                      key={label as string}
                      onClick={() => setPromised(key)}
                      className={cx(
                        'h-10 rounded-full border px-4 text-[15px] font-semibold transition-colors',
                        promised === key
                          ? 'border-brand-600 bg-brand-100 text-brand-700'
                          : 'border-line-strong bg-white text-ink-2 hover:bg-sunken',
                      )}
                    >
                      {label as string}
                    </button>
                  )
                })}
              </div>
            </Field>

            <Field label="备注">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="例：顾客要求牙色偏白一点"
              />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-6">
            <Field label="定金" hint="可以跳过，之后再收">
              <MoneyInput
                autoFocus
                value={deposit}
                onChange={setDeposit}
                invalid={!!deposit && parseAmount(deposit) === null}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {[100, 200, 300, 500].map((v) => (
                  <button
                    key={v}
                    onClick={() => setDeposit(v.toFixed(2))}
                    className="h-10 rounded-full border border-line-strong bg-white px-4 text-[15px] font-semibold text-ink-2 hover:bg-sunken"
                  >
                    {v}
                  </button>
                ))}
                <button
                  onClick={() => setPrice((p) => (setDeposit(p), p))}
                  className="h-10 rounded-full border border-line-strong bg-white px-4 text-[15px] font-semibold text-ink-2 hover:bg-sunken"
                >
                  全额
                </button>
              </div>
            </Field>

            <Field label="收款方式">
              <Segmented
                value={method}
                onChange={setMethod}
                ariaLabel="收款方式"
                options={[
                  { value: 'CASH', label: '现金' },
                  { value: 'DUITNOW', label: 'DuitNow' },
                  { value: 'TRANSFER', label: '转账' },
                ]}
              />
            </Field>

            <label className="flex min-h-11 cursor-pointer items-center gap-3 text-[17px]">
              <input
                type="checkbox"
                checked={sendLink}
                onChange={(e) => setSendLink(e.target.checked)}
                className="size-5.5 accent-brand-600"
              />
              建单后生成进度链接给顾客
            </label>
          </div>
        )}

        {step === 'done' && result && (
          <div className="flex flex-col items-center gap-5 py-4 text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-ok-bg text-ok">
              <CheckIcon size={36} />
            </div>
            <div>
              <p className="text-[17px] text-ink-2">工单已建立</p>
              <p className="tnum text-[36px] font-bold leading-tight">{result.caseNo}</p>
              <p className="text-[17px]">{customer?.fullName}</p>
            </div>

            <div className="tnum w-full max-w-80 rounded-md border border-line bg-sunken px-5 py-4 text-[17px]">
              <Row label="成交价" value={rm(result.finalAmount)} />
              <Row label="已收" value={rm(result.paidAmount)} />
              <div className="my-2 border-t-2 border-line-strong" />
              <Row label="余额" value={rm(result.balanceAmount)} strong />
            </div>

            {result.link && (
              <div className="flex w-full max-w-80 flex-col gap-2">
                <Button
                  block
                  size="lg"
                  variant="primary"
                  icon={<MessageCircle size={20} />}
                  onClick={() => window.open(result.link!.whatsappUrl, '_blank', 'noopener')}
                >
                  用 WhatsApp 发给顾客
                </Button>
                <Button
                  block
                  icon={<Copy size={18} />}
                  onClick={() => {
                    void navigator.clipboard.writeText(result.link!.messageText)
                    toast.show('文案已复制')
                  }}
                >
                  复制文案
                </Button>
              </div>
            )}

            <div className="flex w-full max-w-80 gap-2">
              <Button
                block
                onClick={() => {
                  setStep(1)
                  setCustomer(null)
                  setType(null)
                  setPrice('')
                  setDeposit('')
                  setResult(null)
                }}
              >
                继续开单
              </Button>
              <Button
                block
                variant="secondary"
                onClick={() => {
                  onClose()
                  navigate(`/cases/${result.caseId}`)
                }}
              >
                查看工单
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cx('flex justify-between py-1', strong && 'text-[21px] font-bold')}>
      <span className={strong ? '' : 'text-ink-2'}>{label}</span>
      <span>{value}</span>
    </div>
  )
}

function Steps({ current }: { current: 1 | 2 | 3 }) {
  const labels = ['找顾客', '工单内容', '收定金']
  return (
    <ol className="mb-6 flex items-center gap-1">
      {labels.map((l, i) => {
        const n = (i + 1) as 1 | 2 | 3
        const done = n < current
        const active = n === current
        return (
          <li key={l} className="flex flex-1 items-center gap-1">
            <span className="flex items-center gap-2">
              <span
                className={cx(
                  'flex size-7 shrink-0 items-center justify-center rounded-full text-[14px] font-bold',
                  done || active
                    ? 'bg-brand-600 text-white'
                    : 'border border-line-strong text-ink-3',
                )}
              >
                {done ? <CheckIcon size={15} /> : n}
              </span>
              <span
                className={cx(
                  'text-[15px]',
                  active ? 'font-semibold text-ink' : 'text-ink-3',
                )}
              >
                {l}
              </span>
            </span>
            {i < 2 && <span className="h-px flex-1 bg-line-strong" />}
          </li>
        )
      })}
    </ol>
  )
}

/** 步骤 1：搜索优先。找不到就地建档，不跳出流程。 */
function PickCustomer({
  value,
  onPick,
}: {
  value: { id: string; fullName: string } | null
  onPick: (c: { id: string; fullName: string }) => void
}) {
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [newOpen, setNewOpen] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300)
    return () => clearTimeout(t)
  }, [q])

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: () =>
      api.get<{ items: SearchItem[] }>(
        `/api/customers/search?q=${encodeURIComponent(debounced)}&limit=8`,
      ),
    // 空关键词返回最近顾客，省得每次都要先想起名字
    enabled: true,
    placeholderData: (p) => p,
  })

  const items = useMemo(() => results.data?.items ?? [], [results.data])

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-md border-2 border-brand-600 bg-brand-50 px-4 py-4">
        <Avatar name={value.fullName} size={44} />
        <span className="flex-1 text-[19px] font-semibold">{value.fullName}</span>
        <Button size="sm" onClick={() => onPick(null as never)}>
          换一位
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <Field label="找顾客">
          <div className="relative">
            <Search
              size={20}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3"
            />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="电话后 4 位 / 姓名"
              className="pl-11"
            />
          </div>
        </Field>

        {debounced.trim().length >= 2 && items.length === 0 && !results.isFetching && (
          <div className="rounded-md border border-dashed border-line-strong px-4 py-6 text-center">
            <p className="mb-3 text-[16px] text-ink-2">找不到「{debounced}」</p>
            <Button variant="primary" icon={<UserPlus size={20} />} onClick={() => setNewOpen(true)}>
              新建这位顾客
            </Button>
          </div>
        )}

        <ul className="flex flex-col gap-1">
          {items.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => onPick({ id: c.id, fullName: c.fullName })}
                className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-sunken"
              >
                <Avatar name={c.fullName} size={40} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[17px] font-semibold">{c.fullName}</span>
                  <span className="tnum block text-[15px] text-ink-2">
                    {c.phoneMasked} · {c.caseCount} 张工单
                  </span>
                </span>
                {c.hasOutstanding && <Badge tone="warn">有欠款</Badge>}
              </button>
            </li>
          ))}
        </ul>

        <Button icon={<UserPlus size={20} />} onClick={() => setNewOpen(true)}>
          新建顾客
        </Button>
      </div>

      <NewCustomerDrawer
        open={newOpen}
        initialPhone={/^\d+$/.test(debounced) ? debounced : ''}
        initialName={/^\d+$/.test(debounced) ? '' : debounced}
        onClose={() => setNewOpen(false)}
        onCreated={(id, name) => onPick({ id, fullName: name })}
      />
    </>
  )
}

export function useNewCaseDrawer() {
  const [open, setOpen] = useState(false)
  return { open, setOpen }
}
